// TelcoTrack — Pasportizace (photos bound to coordinates only, not map objects)

import { isPasportizaceDrawing } from './drawing.constants';

/** Stable map overlay / prefs id (Vrstvy toggle). */
export const PASPORTIZACE_LAYER_ID = 'pasportizace' as const;

/** Czech UI label for layer + entry points. */
export const PASPORTIZACE_LAYER_LABEL = 'Pasportizace';

/** Capture source stored on each row. */
export const PASPORTIZACE_SOURCES = {
  CAMERA: 'camera',
  GALLERY: 'gallery',
} as const;

export type PasportizaceSource =
  (typeof PASPORTIZACE_SOURCES)[keyof typeof PASPORTIZACE_SOURCES];

export const PASPORTIZACE_SOURCE_VALUES: readonly PasportizaceSource[] = [
  PASPORTIZACE_SOURCES.CAMERA,
  PASPORTIZACE_SOURCES.GALLERY,
];

/** MinIO path prefix (same bucket as object photos). */
export const PASPORTIZACE_STORAGE_PREFIX = 'pasportizace';

/** Thumbnail max edge (px) generated server-side with sharp. */
export const PASPORTIZACE_THUMBNAIL_MAX_EDGE_PX = 256;

/** Czech UI copy. */
export const PASPORTIZACE_UI = {
  LAYER_LABEL: PASPORTIZACE_LAYER_LABEL,
  UPLOAD_PHOTOS: 'Nahrát fotky',
  WALK_CAMERA: 'Fotit při chůzi',
  NO_GPS_IN_METADATA: 'Fotka bez GPS v metadatech — nelze nahrát.',
  NO_GPS_NAMED: (fileName: string) =>
    `Fotka bez GPS v metadatech: ${fileName}`,
  UPLOAD_PARTIAL_FAIL: (ok: number, fail: number) =>
    `Nahráno ${ok}, přeskočeno ${fail} (bez GPS v metadatech).`,
  UPLOAD_ALL_FAIL: 'Žádná fotka nemá GPS v metadatech — nic nebylo nahráno.',
  LAYER_GROUP: 'Pasportizace',
  PREFS_KEY: 'pasportizace_layer_enabled',
} as const;

/** Map filter id prefix for the Vrstvy multi-select (parity with `dtm:`). */
export const MAP_FILTER_PASPORTIZACE_ID = `overlay:${PASPORTIZACE_LAYER_ID}` as const;

/**
 * Pasportizace markers show only on the dedicated `pasportizace` výkres.
 * Prefs stay global; map sync / bbox load apply only when this returns true.
 */
export function isPasportizaceVisibleOnDrawing(drawingId: string | null | undefined): boolean {
  return isPasportizaceDrawing(drawingId);
}

/**
 * Gate for bbox fetch + marker render.
 * `minZoom` should be the same detail threshold as address points
 * (`MAP_ZOOM.addressPointsMinZoom` / Android `ADDRESS_POINTS_MIN_ZOOM`).
 */
export function shouldLoadPasportizaceOverlay(opts: {
  layerEnabled: boolean;
  drawingId: string | null | undefined;
  zoom: number;
  minZoom: number;
}): boolean {
  return (
    opts.layerEnabled &&
    isPasportizaceVisibleOnDrawing(opts.drawingId) &&
    Number.isFinite(opts.zoom) &&
    opts.zoom >= opts.minZoom
  );
}

export function isPasportizaceSource(value: unknown): value is PasportizaceSource {
  return (
    typeof value === 'string' &&
    (PASPORTIZACE_SOURCE_VALUES as readonly string[]).includes(value)
  );
}

/** WGS84 lon/lat sanity (same bounds as object bbox API). */
export function isValidPasportizaceLngLat(lng: unknown, lat: unknown): boolean {
  const lon = typeof lng === 'number' ? lng : Number(lng);
  const la = typeof lat === 'number' ? lat : Number(lat);
  return (
    Number.isFinite(lon) &&
    Number.isFinite(la) &&
    lon >= -180 &&
    lon <= 180 &&
    la >= -90 &&
    la <= 90
  );
}

export function getPasportizaceStorageKey(fileName: string): string {
  return `${PASPORTIZACE_STORAGE_PREFIX}/${fileName}`;
}

export function getPasportizaceThumbnailKey(fileName: string): string {
  const base = fileName.replace(/\.[^.]+$/, '') || fileName;
  return `${PASPORTIZACE_STORAGE_PREFIX}/thumbs/${base}.jpg`;
}
